import type { IPointerController, IPointerHandler } from '@/components/viewPort/PointerController';
import type { SyntheticEvent } from '@/components/viewPort/SyntheticEvent';
import * as THREE from 'three';
import type { SliceType } from './interfaces';
import { ImagesMode } from './interfaces';
import type { ImagesService } from './Images.service';
import * as GeometryHelper from '@/engine/utils/geometryHelper';
import type { ObservabilityLoggerService } from '@/app.obresvability/loggerService/observabilityLogger.service.ts';
import { throttle } from 'underscore';

export enum MprModes {
  MOVE = 1,
  ROTATE = 2
}

export interface ICursorData {
  cursor2D: THREE.Vector2;
  cursor3D: THREE.Vector3;
  cursorPoint: THREE.Vector3;
}

export interface IMprCursorDataProvider {
  getCursorData: (event: SyntheticEvent<PointerEvent>) => ICursorData;
}

export class PointerHandlerAdjustMpr implements IPointerHandler {
  private _parent?: IPointerController;

  private readonly _imagesService: ImagesService;

  private readonly _cursorDataProvider: IMprCursorDataProvider;

  private readonly _loggerService: ObservabilityLoggerService;

  private readonly _mainSliceType: SliceType;

  private _mprMode: MprModes;

  private _slicesIntersectionPoint?: THREE.Vector3;

  private _slicesOrientationsMementos?: Map<SliceType, THREE.Matrix4>;

  private _cursorData?: ICursorData;

  private _isActive = false;

  private _interval?: NodeJS.Timeout;

  private _wasPerformed = false;

  public constructor(
    imagesService: ImagesService,
    MprMode: MprModes,
    mainSliceType: SliceType,
    cursorDataProvider: IMprCursorDataProvider,
    loggerService: ObservabilityLoggerService
  ) {
    this._imagesService = imagesService;
    this._mprMode = MprMode;
    this._mainSliceType = mainSliceType;
    this._cursorDataProvider = cursorDataProvider;
    this._loggerService = loggerService;

    this._applyMpr = throttle(this._applyMpr.bind(this), 100);
  }

  public setParent(parent: IPointerController): void {
    this._parent = parent;
  }

  public onPointerDown(event: SyntheticEvent<PointerEvent>): void {
    if (event.originalEvent.button != 0) {
      return;
    }
    if (
      this._imagesService.imagesMode === ImagesMode.POLYLINE &&
      this._mainSliceType !== this._imagesService.getPolylineInfo()?.slice
    ) {
      return;
    }
    event.stopPropagation();
    event.preventDefault();
    this._slicesIntersectionPoint = this._imagesService.getSlicesIntersectionPoint();
    this._slicesOrientationsMementos = this._imagesService.getSlicesOrientationsMementos();
    this._parent?.setPointerCapture(event.originalEvent.pointerId);
    this._cursorData = this._cursorDataProvider.getCursorData(event);
    this._isActive = true;
    this._wasPerformed = false;
    this._loggerService.dispatchImageAction('MPR rotation', this._mainSliceType);
  }

  public onPointerUp(event: SyntheticEvent<PointerEvent>): void {
    if (event.originalEvent.button != 0) {
      return;
    }
    event.stopPropagation();
    event.preventDefault();
    if (this._interval) {
      clearInterval(this._interval);
      this._interval = undefined;
    }
    if (!this._wasPerformed && this._slicesIntersectionPoint && this._slicesOrientationsMementos) {
      const { cursorPoint } = this._cursorDataProvider.getCursorData(event);
      this._applyMpr(cursorPoint);
      this._mprMode = MprModes.MOVE;
    }
    this._slicesIntersectionPoint = undefined;
    this._slicesOrientationsMementos = undefined;
    this._isActive = false;
    this._wasPerformed = false;
    this._cursorData = undefined;
    this._parent?.releasePointerCapture(event.originalEvent.pointerId);
  }

  public onPointerCancel(event: SyntheticEvent<PointerEvent>): void {
    this._isActive = false;
    this._wasPerformed = false;
    if (this._interval) {
      clearInterval(this._interval);
      this._interval = undefined;
    }
    this._parent?.releasePointerCapture(event.originalEvent.pointerId);
  }

  public onPointerMove(event: SyntheticEvent<PointerEvent>): void {
    if (!this._isActive) {
      return;
    }
    event.stopPropagation();
    event.preventDefault();
    if (this._interval) {
      clearInterval(this._interval);
      this._interval = undefined;
    }
    this._wasPerformed = this._performAction(event);
    if (this._wasPerformed) {
      this._interval = setInterval(() => {
        if (!this._performAction(event)) {
          clearInterval(this._interval);
          this._interval = undefined;
        }
      }, 50);
    }
  }

  private _applyMpr(origin: THREE.Vector3, rotationMatrix?: THREE.Matrix4): void {
    if (this._slicesOrientationsMementos) {
      this._imagesService.applyMpr(this._slicesOrientationsMementos, this._mainSliceType, origin, rotationMatrix);
    }
  }

  private _performAction(event: SyntheticEvent<PointerEvent>): boolean {
    const { cursorPoint } = this._cursorDataProvider.getCursorData(event);
    switch (this._mprMode) {
      case MprModes.MOVE:
        return this._performMove(cursorPoint);
      case MprModes.ROTATE:
        return this._performRotate(cursorPoint);
      default:
        throw new Error('Unknown MPR Mode');
    }
  }

  private _performMove(cursorPoint: THREE.Vector3): boolean {
    if (
      !this._cursorData ||
      this._cursorData.cursorPoint.equals(cursorPoint) ||
      !this._slicesIntersectionPoint ||
      !this._slicesOrientationsMementos
    ) {
      return false;
    }
    const movementVector = new THREE.Vector3().subVectors(cursorPoint, this._cursorData.cursorPoint);
    const newIntersectionPoint = new THREE.Vector3().addVectors(this._slicesIntersectionPoint, movementVector);

    this._applyMpr(newIntersectionPoint);

    return true;
  }

  private _performRotate(cursorPoint: THREE.Vector3): boolean {
    if (
      !this._cursorData ||
      this._cursorData.cursorPoint.equals(cursorPoint) ||
      !this._slicesIntersectionPoint ||
      !this._slicesOrientationsMementos
    ) {
      return false;
    }
    const content = this._imagesService.getSlicePositionInfo(this._mainSliceType);
    if (!content) throw new Error('Invalid Slice Type');
    const originalVector = new THREE.Vector3().subVectors(this._cursorData.cursorPoint, this._slicesIntersectionPoint);
    const rotatedVector = new THREE.Vector3().subVectors(cursorPoint, this._slicesIntersectionPoint);
    let angleRadians = originalVector.angleTo(rotatedVector);
    const dir = new THREE.Vector3().crossVectors(originalVector, rotatedVector);
    const normal = new THREE.Vector3().setFromMatrixColumn(content.sliceCS, 2);
    if (dir.dot(normal) < 0) {
      angleRadians = -angleRadians;
    }

    const _cs = this._slicesOrientationsMementos.get(this._mainSliceType);
    if (!_cs) {
      throw new Error('Invalid Slice Type');
    }
    const _rotationMatrix = GeometryHelper.makeRotationMatrix(
      this._slicesIntersectionPoint,
      new THREE.Vector3().setFromMatrixColumn(_cs, 2),
      angleRadians
    );

    // for (const _sliceType in this._activeModeCache.viewsCamerasMementos) {
    //   if (_sliceType != sliceType) {
    //     const memento = this._activeModeCache.viewsCamerasMementos[_sliceType];
    //     this._threeDViews[_sliceType].applyCameraMemento({
    //       position: commonModules.geometryHelper.applyTransformation(
    //         memento.position,
    //         _rotationMatrix,
    //         false
    //       ),
    //       upVector: commonModules.geometryHelper.applyTransformation(
    //         memento.upVector,
    //         _rotationMatrix,
    //         true
    //       ),
    //       targetVector: commonModules.geometryHelper.applyTransformation(
    //         memento.targetVector,
    //         _rotationMatrix,
    //         true
    //       ),
    //     });
    //   }
    // }
    this._applyMpr(this._slicesIntersectionPoint, _rotationMatrix);

    return true;
  }
}
